import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppUtilityService } from '../../../shared/services/app-utility.service';
import { selectAppState, selectPanelName, selectReportContentItemsSlice, selectStudy, selectIsStudySaved } from '../../state/selectors';
import { Study } from '../../../shared/models/study.model';
import {  ReportContentItem } from '../../../shared/models/run.model';
import { ColumnSelection } from '../../../shared/models/dialog.model';
import {
  applyPrintColumnSelection,
  chunkColumnsForPrint,
  describePrintPage,
  PrintPageFit
} from '../../../shared/utils/print-column.util';
import { combineLatest, filter, Subject, takeUntil } from 'rxjs';
import { getReportContentResultTypeName, getStatusClassFromResult, trackByFn } from '../../../shared/utils/assay-helper ';
import { AssayDataCalCurve, CalculationResult, CalibratorAssay, CalibratorDataIndex, CalibratorSample, RunStatus } from '../../../shared/models/result.model';
import { Assay_Specs, Panel } from '../../../shared/models/assays.model';
import { CalLot } from '../../../shared/models/cal-lot.model';
import { formatCalibratorDataSequential, getConcentrationByRegion } from '../../../shared/utils/calibrator-helper';
import { adjustedColumnLabel, saveBlobAsFile } from '../../../shared/utils/helper';
import { toCsv } from '../../../shared/utils/csv.util';
import { isCalAdjustFinal } from '../../../shared/utils/run-type.util';
/**
 * Component displaying detailed calibration data for calibrator assays.
 * Provides data filtering, column selection, table exports, and print-ready views.
 */
@Component({
  selector: 'app-calibration-details',
  templateUrl: './calibration-details.component.html',
  styleUrl: './calibration-details.component.scss'
})
export class CalibrationDetailsComponent implements OnInit, OnDestroy, AfterViewInit {
  isStudySaved$ = this.store.select(selectIsStudySaved);
  private readonly internalQcBeadRegions = new Set<number>([34, 98]);
  @ViewChild('calibrationDetailsScroll', { static: false }) calibrationDetailsScrollRef?: ElementRef<HTMLDivElement>;
  @ViewChild('calibrationDetailsTable', { static: false }) calibrationDetailsTableRef?: ElementRef<HTMLTableElement>;

  selectedStudy$ = this.store.select(selectStudy);
  reportContentItems$ = this.store.select(selectReportContentItemsSlice);
  panelName$ = this.store.select(selectPanelName);
  protected destroy$ = new Subject<boolean>();
  selectedStudy!: Study | null;
  /** Whether the adjusted columns hold a Calibrator Adjustment's Final run. */
  isFinal = false;

  get adjustedLabel(): string {
    return adjustedColumnLabel(this.isFinal);
  }
  sectionVisibility = new Array<boolean>();
  isPrintView = false;
  isPrintMode = false;
  isMenuOpen = false;
  operatorName = '';
  appVersion = '';
  reportContent: ReportContentItem[] | null = [];
  filteredReportContent: ReportContentItem[] = [];
  tableColumns!: ColumnSelection[];
  selectedColumns!: ColumnSelection[];
  /** Null until the operator narrows the print selection; see `applyPrintColumnSelection`. */
  private printColumnSelection: ColumnSelection[] | null = null;
  noOfRows: number[] = [1, 2, 3, 4, 5, 6, 7, 8];
  noOfRowAlphabet: string[] = ['a', 'b', 'c', 'd'];
  /**
   * "Calibrator (Avg Adjusted FR)" is the widest row label; the data columns hold one number each.
   * Chunking is derived from these, not from a column count.
   */
  readonly printPageFit: PrintPageFit = { labelWidthMm: 52, columnWidthMm: 22 };
  calculationResult!: CalculationResult | null | undefined;
  calculationAdjustedResult!: CalculationResult | null | undefined;
  panelSpecs!: Panel | null;
  calLots!: CalLot[];

  trackByFn = trackByFn;
  trackByPage = (index: number) => index;
  getReportContentResultTypeName = getReportContentResultTypeName;
  getStatusClassFromResult = getStatusClassFromResult;

  calResponseCombinedData: CalibratorAssay[] = [];
  calResponseCombinedBackupData: CalibratorAssay[] = [];
  calResponseFilteredData: CalibratorAssay[] = [];
  calResponseFilteredAdjustedData: CalibratorAssay[] = [];
  private headerResizeObserver?: ResizeObserver;
  /**
   * 
   * @param router Angular router
   * @param store NGRX store
   * @param appUtilityService Utility service 
   * @param cdRef Change detector reference
   */
  constructor(
    private router: Router,
    private store: Store,
    private appUtilityService: AppUtilityService,
    public cdRef: ChangeDetectorRef
  ) { }
  /** `ngOnInit` is a lifecycle hook called during the initialization of the component. */
  ngOnInit() {
    this.isPrintView = this.router.url.includes('print');
    // The print route is print mode; there is no other reason to be on it. This used to be
    // switched on by the print menu opening over the page, which left the layout in screen mode
    // if the operator dismissed the menu.
    this.isPrintMode = this.isPrintView;

    combineLatest([this.selectedStudy$, this.reportContentItems$, this.store.select(selectAppState)]).pipe(
      filter(([selectedStudy]) => !!selectedStudy),
      takeUntil(this.destroy$)
    ).subscribe(([selectedStudy, reportContent, appState]) => {
      this.selectedStudy = selectedStudy;
      this.isFinal = isCalAdjustFinal(selectedStudy);
      this.operatorName = appState?.loggedInUser ?? '';
      this.appVersion = appState?.appVersion ?? '';
      const reportContentItems = reportContent?.reportContentItems ?? [];
      const sortedReportContent = this.sortReportContent(reportContentItems);
      this.reportContent = sortedReportContent;
      this.filteredReportContent = sortedReportContent;
      this.panelSpecs = this.selectedStudy.panelSpecs || null;
      this.calLots = this.selectedStudy.calLots;
      this.calculationResult = this.selectedStudy.calculationResponse?.calculationResponse;
      this.calculationAdjustedResult = this.selectedStudy.calculationAdjustedResponse?.calculationResponse;
      this.initialSetData(this.calculationResult, 'OriginalCalculate');
      if (this.calculationAdjustedResult) {
        this.initialSetData(this.calculationAdjustedResult, 'AdjustedCalculate');
      }
      // Re-key any print selection onto the rebuilt columns. These come back fully selected, so
      // without this a rebuild — which the audit write on Print itself can cause — would send
      // every calibrator to the printer and re-tick the preview bar's checkboxes.
      this.tableColumns = applyPrintColumnSelection(
        this.buildTableColumns(this.reportContent ?? []),
        this.printColumnSelection
      );

      if (this.isPrintView) {
        this.handleSelectColumns(this.tableColumns);
      }

      this.sectionVisibility = new Array(16).fill(true);
      this.cdRef.detectChanges();
    });
  }

  ngAfterViewInit(): void {
    this.setupHeaderHeightObserver();
  }
  /**
 * Filters calibrator data based on currently selected columns for table display.
 * @param selectedColumns - Array of columns to display in the table.
 */
  handleSelectColumns(selectedColumns: ColumnSelection[]) {
    this.selectedColumns = selectedColumns;
    const selectedKeys = new Set(
      this.selectedColumns
        .filter(column => column.isSelected)
        .map(column => column.columnKey ?? column.columnName)
    );

    this.calResponseCombinedData = this.calResponseCombinedBackupData.filter((item) =>
      selectedKeys.has(this.getReportContentSelectionKey(item.reportContentItems))
    );
  }
  /**
   * Initializes calibrator assay data using a calculation result.
   * Combines original and adjusted results for unified processing and visualization.
   * @param calculationResult - The result object containing calibrator calculations.
   * @param label - A label identifying the type of calculation (e.g. 'OriginalCalculate', 'AdjustedCalculate').
   */
  initialSetData(calculationResult: CalculationResult | null | undefined, label: string) {
    const result: CalibratorAssay[] = [];

    this.filteredReportContent.forEach((reportContentItem) => {

      const calRunStatus = calculationResult?.runStatus.find(
        (cal) => cal.reportContentItemId === reportContentItem.reportContentItemId
      );
      const assaySpec: Assay_Specs[] | undefined = this.panelSpecs?.Assay_Specs.filter(
        (spec) => spec.bead_region === reportContentItem.region
      );

      const runStatus: RunStatus | undefined = calculationResult?.runStatus.find(
        (run) => run.reportContentItemId === reportContentItem.reportContentItemId
      );
      const region = getConcentrationByRegion(reportContentItem.region, runStatus, this.calLots.find(cl => cl.reagentLot === "All" || cl.reagentLot === reportContentItem.lot)?.vials);
      const resultItem: CalibratorAssay = {
        reportContentItems: reportContentItem,
        calibratorData: region,
        valueAssigned: label
      };

      if (assaySpec && assaySpec.length > 0) {
        resultItem.calibrationCurve = {
          assay_range_lower: assaySpec[0].assay_range_lower,
          assay_range_upper: assaySpec[0].assay_range_upper,
          cut_off: assaySpec[0].cut_off,
          result_decimal_precision: assaySpec[0].result_decimal_precision,
          math_model: assaySpec[0].math_model,
          curve_direction: assaySpec[0].curve_direction,
          monotonic: calRunStatus?.calCurveInformation.monotonic,
          cal_level_for_cv: assaySpec[0].calibrator_used_for_bead_to_bead_cv,
        }
      }

      result.push(resultItem);
    });

    if (label === 'OriginalCalculate') {
      // A Final run has no unadjusted baseline, so no unadjusted columns are shown.
      this.calResponseFilteredData = this.isFinal ? [] : result;
    } else {
      this.calResponseFilteredAdjustedData = result;
    }

    if (this.calResponseFilteredAdjustedData.length > 0) {
      this.calResponseCombinedData = this.getCombinedData();
    } else {
      this.calResponseCombinedData = this.calResponseFilteredData;
    }
    this.calResponseCombinedData = formatCalibratorDataSequential(this.calResponseCombinedData);
    this.calResponseCombinedBackupData = this.calResponseCombinedData;
  }
  /**
   * Retrieves a specific field value for a calibrator sample based on its row index and field key.
   * @param rowIndex - Index representing the calibrator row.
   * @param materialItem - The calibrator assay containing the data.
   * @param field - The field name to retrieve.
   * @returns The value from the calibrator data, or empty string if not found.
   */
  getCalibratorValue(rowIndex: number, materialItem: CalibratorAssay, field: keyof CalibratorSample): string | number | undefined {
    return this.displayDash(this.getRawCalibratorValue(rowIndex, materialItem, field));
  }
  /**
   * Returns the raw calibrator value without formatting for display.
   */
  getRawCalibratorValue(rowIndex: number, materialItem: CalibratorAssay, field: keyof CalibratorSample): unknown {
    const rowData = materialItem.fullCalibratorData?.find((item: CalibratorDataIndex) => item.id === rowIndex);
    return rowData?.data?.[field];
  }
  /**
   * Displays a dash for null/undefined/empty values.
   */
  displayDash(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    return String(value);
  }
  /**
   * Checks whether any calibrator row has data for the given field.
   */
  rowHasAnyCalibratorValue(rowIndex: number, field: keyof CalibratorSample): boolean {
    return this.calResponseCombinedData.some(item => this.hasValue(this.getRawCalibratorValue(rowIndex, item, field)));
  }
  /**
   * Checks whether any column has a calibration curve value for the given field.
   */
  rowHasAnyCalibrationCurveValue(field: keyof AssayDataCalCurve): boolean {
    return this.calResponseCombinedData.some(item => this.hasValue(item?.calibrationCurve?.[field]));
  }
  /**
   * Checks whether any column has a report-content value for the given field.
   */
  rowHasAnyReportContentValue(field: keyof ReportContentItem): boolean {
    return this.calResponseCombinedData.some(item => this.hasValue(item?.reportContentItems?.[field]));
  }
  private hasValue(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '';
  }
  /**
   * Merges original and adjusted calibrator datasets into a single combined list.
   * @returns Combined list of calibrator assay data.
   */
  getCombinedData() {
    const combined = [
      ...(this.calResponseFilteredData ?? []),
      ...(this.calResponseFilteredAdjustedData ?? [])
    ];
    return combined.sort((left, right) => this.compareCalibratorAssay(left, right));
  }

  private sortReportContent(reportContent: ReportContentItem[]): ReportContentItem[] {
    return [...(reportContent ?? [])].sort((a, b) => this.compareReportContentItems(a, b));
  }

  private compareReportContentItems(a: ReportContentItem, b: ReportContentItem): number {
    const baseCompare = this.compareReportContentBase(a, b);
    if (baseCompare !== 0) {
      return baseCompare;
    }

    const adjustmentCompare =
      this.getReportContentAdjustmentRank(a) - this.getReportContentAdjustmentRank(b);
    if (adjustmentCompare !== 0) {
      return adjustmentCompare;
    }

    const runCompare = (a.runId ?? '').localeCompare(b.runId ?? '');
    if (runCompare !== 0) {
      return runCompare;
    }

    return (a.assay ?? '').localeCompare(b.assay ?? '');
  }

  private compareReportContentBase(a: ReportContentItem, b: ReportContentItem): number {
    const regionCompare = this.compareBeadRegions(a.region, b.region);
    if (regionCompare !== 0) {
      return regionCompare;
    }

    const refLot = (this.selectedStudy?.refKitLot ?? '').trim();
    const aLot = (a.lot ?? '');
    const bLot = (b.lot ?? '');
    if (refLot) {
      const aIsRef = aLot === refLot;
      const bIsRef = bLot === refLot;
      if (aIsRef !== bIsRef) {
        return aIsRef ? -1 : 1;
      }
    }
    const kitCompare = aLot.localeCompare(bLot);
    if (kitCompare !== 0) {
      return kitCompare;
    }

    return (a.assay ?? '').localeCompare(b.assay ?? '');
  }

  private compareCalibratorAssay(left: CalibratorAssay, right: CalibratorAssay): number {
    const baseCompare = this.compareReportContentBase(left.reportContentItems, right.reportContentItems);
    if (baseCompare !== 0) {
      return baseCompare;
    }

    const adjustCompare = this.getCalibratorAdjustmentRank(left) - this.getCalibratorAdjustmentRank(right);
    if (adjustCompare !== 0) {
      return adjustCompare;
    }

    const runCompareWithMean = this.compareRunIdWithMeanLast(left, right);
    if (runCompareWithMean !== 0) {
      return runCompareWithMean;
    }

    return 0;
  }

  private compareRunIdWithMeanLast(left: CalibratorAssay, right: CalibratorAssay): number {
    const leftIsMean = this.isMeanReportItem(left.reportContentItems.reportContentItemId);
    const rightIsMean = this.isMeanReportItem(right.reportContentItems.reportContentItemId);
    if (leftIsMean !== rightIsMean) {
      return leftIsMean ? 1 : -1;
    }
    return (left.reportContentItems.runId ?? '').localeCompare(right.reportContentItems.runId ?? '');
  }

  private getCalibratorAdjustmentRank(item: CalibratorAssay): number {
    return (item as unknown as { valueAssigned?: string }).valueAssigned === 'AdjustedCalculate' ? 1 : 0;
  }

  private getReportContentAdjustmentRank(item: ReportContentItem): number {
    return item?.['valueAssigned'] === 'AdjustedCalculate' ? 1 : 0;
  }

  private isMeanReportItem(reportContentItemId: string | undefined): boolean {
    return !!reportContentItemId && reportContentItemId.startsWith('Mean_');
  }

  private compareBeadRegions(left: string | number | undefined, right: string | number | undefined): number {
    const leftRegion = this.parseRegionValue(left);
    const rightRegion = this.parseRegionValue(right);
    const leftInternal = this.internalQcBeadRegions.has(leftRegion);
    const rightInternal = this.internalQcBeadRegions.has(rightRegion);
    if (leftInternal !== rightInternal) {
      return leftInternal ? 1 : -1;
    }
    return leftRegion - rightRegion;
  }

  private parseRegionValue(region: string | number | undefined): number {
    if (typeof region === 'number') {
      return Number.isNaN(region) ? 0 : region;
    }
    const parsed = Number(region);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private buildTableColumns(reportContent: ReportContentItem[]): ColumnSelection[] {
    const columnsByKey = new Map<string, ColumnSelection>();

    reportContent.forEach(item => {
      const key = this.getReportContentSelectionKey(item);
      if (!columnsByKey.has(key)) {
        columnsByKey.set(key, {
          columnName: key,
          columnKey: key,
          kitLot: item.lot ?? '-',
          instrumentId: item.runId ?? '-',
          beadRegion: item.region ?? '-',
          isSelected: true
        });
      }
    });

    return Array.from(columnsByKey.values());
  }

  private getReportContentSelectionKey(item: ReportContentItem): string {
    const reportContentId = item.reportContentItemId?.trim();
    if (reportContentId) {
      return reportContentId;
    }
    return [item.lot ?? '', item.runId ?? '', item.region ?? '', item.assay ?? ''].join('|');
  }
  /**
   * Opens or closes all section panels depending on print mode.
   * @param isMenuOpen - Boolean indicating whether the print menu is open.
   */
  handleMenuOpenStatus(isMenuOpen: boolean) {
    this.isPrintMode = isMenuOpen;

    if (isMenuOpen) {
      this.toggleAllSections(isMenuOpen);
    }
  }

  /** Columns chosen in the print preview bar; held so a rebuild cannot silently widen the print. */
  handlePrintColumnSelection(selectedColumns: ColumnSelection[]): void {
    this.printColumnSelection = selectedColumns;
    this.handleSelectColumns(selectedColumns);
  }

  /** Identifies the document in the print preview bar, beside the study name. */
  get printContextLine(): string {
    const specVersion = this.selectedStudy?.panelSpecs?.revisionNumber ?? '';
    return [
      this.selectedStudy?.runType,
      this.selectedStudy?.testSpec ? `Test Spec ${this.selectedStudy.testSpec}` : '',
      specVersion ? `Spec Rev ${specVersion}` : '',
      this.operatorName
    ].filter(Boolean).join(' · ');
  }

  get printPages(): CalibratorAssay[][] {
    return chunkColumnsForPrint(this.calResponseCombinedData, this.printPageFit);
  }

  /** "Columns 7-12 of 22 · page 2 of 4" over a chunk page; empty when the report was not split. */
  printPageCaption(pageIndex: number): string {
    return describePrintPage(pageIndex, this.printPages);
  }
  /**
   * Toggles the display visibility for a specific table section by index.
   * @param sectionIndex - Index of the section to toggle.
   */
  toggleSectionDisplay(sectionIndex: number) {
    this.sectionVisibility[sectionIndex] = !this.sectionVisibility[sectionIndex];
  }
  /**
   * Expands or collapses all calibration sections.
   * @param isExpand - Boolean indicating whether to expand (true) or collapse (false) all sections.
   */
  toggleAllSections(isExpand: boolean) {
    this.sectionVisibility = this.sectionVisibility.map(() => isExpand);
  }
  /** Downloads the table as CSV: the row labels down the left, one column per kit lot / instrument / assay. */
  exportToCsv(): void {
    const rows = this.buildExportRows();
    if (!rows.length) {
      return;
    }
    const csv = toCsv(rows[0], rows.slice(1));
    saveBlobAsFile(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'calibration-assays.csv');
  }

  /**
   * The table's rows as text, with the same rows left out as on screen. Every section is included
   * whether or not it is collapsed, since collapsing only hides it from view.
   */
  buildExportRows(): string[][] {
    const columns = this.calResponseCombinedData;
    const rows: string[][] = [];
    const sectionHeader = (title: string) => rows.push([title, ...columns.map(() => '')]);

    const reportContentRows: { label: string; field: keyof ReportContentItem }[] = [
      { label: 'KitLot#', field: 'lot' },
      { label: 'Instrument ID', field: 'runId' },
      { label: 'Assay Name', field: 'assay' },
      { label: 'Bead Region', field: 'region' },
      { label: 'Units', field: 'units' },
      { label: 'Result Decimal Precision', field: 'resultDecimalPrecision' },
      { label: 'User Defined Decimal Precision', field: 'userDefinedDecimalPrecision' },
      { label: 'Result Type', field: 'resultType' }
    ];
    reportContentRows.filter(({ field }) => this.rowHasAnyReportContentValue(field)).forEach(({ label, field }) => {
      rows.push([label, ...columns.map(column => {
        const value = this.displayDash(column.reportContentItems[field]);
        return field === 'lot' && column.valueAssigned === 'AdjustedCalculate' ? `${value} ${this.adjustedLabel}` : value;
      })]);
    });

    sectionHeader('Calibration Curve');
    const curveRows: { label: string; field: keyof AssayDataCalCurve }[] = [
      { label: 'Low Assay Range', field: 'assay_range_lower' },
      { label: 'High Assay Range', field: 'assay_range_upper' },
      { label: 'Assay Cut-Off', field: 'cut_off' },
      { label: 'Result Decimal Precision', field: 'result_decimal_precision' },
      { label: 'Math Model', field: 'math_model' },
      { label: 'Curve Direction', field: 'curve_direction' },
      { label: 'Monotonic Pass/Fail', field: 'monotonic' }
    ];
    curveRows.filter(({ field }) => this.rowHasAnyCalibrationCurveValue(field)).forEach(({ label, field }) => {
      rows.push([label, ...columns.map(column => {
        const value = this.displayDash(column.calibrationCurve?.[field]);
        return field === 'monotonic' ? value.toUpperCase() : value;
      })]);
    });

    // A calibrator section lists vials 1-8, skipping a vial no column has a value for.
    const calibratorSections: { title: string; fields: (keyof CalibratorSample)[]; fieldFor?: (column: CalibratorAssay) => keyof CalibratorSample }[] = [
      { title: 'Calibrator Vial', fields: ['index'] },
      {
        title: 'Calibrator Assignment',
        fields: ['originalAssignment', 'optimizedAssignment'],
        fieldFor: column => column.valueAssigned === 'AdjustedCalculate' && !this.isFinal ? 'optimizedAssignment' : 'originalAssignment'
      },
      { title: 'Calibrator (Avg RFI)', fields: ['meanRfi'] },
      { title: 'Calibrator (Avg FR)', fields: ['meanFR'] },
      { title: 'Calibrator (Avg Adjusted FR)', fields: ['adjustedFr'] },
      { title: 'Calibrator (Replicate CV)', fields: ['repRfiCV'] },
      { title: 'Calibrator CV (Pass/Fail)', fields: ['repRfiCvOk'] }
    ];
    calibratorSections.forEach(({ title, fields, fieldFor }) => {
      sectionHeader(title);
      this.noOfRows
        .filter(vial => fields.some(field => this.rowHasAnyCalibratorValue(vial, field)))
        .forEach(vial => {
          rows.push([String(vial), ...columns.map(column => String(this.getCalibratorValue(vial, column, fieldFor ? fieldFor(column) : fields[0])))]);
        });
    });
    return rows;
  }

  /**
   * Navigates to a print-friendly route for calibration assays.
   */
  navigateToPrintView() {
    this.router.navigateByUrl('/(print:print/calibration-assays)');
  }
  /** `ngOnDestroy` is a lifecycle hook that is triggered when the component is destroyed.  */
  ngOnDestroy() {
    this.destroy$.next(true);
    this.headerResizeObserver?.disconnect();
    this.destroy$.unsubscribe();
  }

  private setupHeaderHeightObserver(): void {
    const scrollEl = this.calibrationDetailsScrollRef?.nativeElement;
    const tableEl = this.calibrationDetailsTableRef?.nativeElement;
    if (!scrollEl || !tableEl) {
      return;
    }

    const headerEl = tableEl.querySelector('thead');
    if (!headerEl) {
      return;
    }

    const updateHeaderHeight = () => {
      const height = headerEl.getBoundingClientRect().height;
      if (height > 0) {
        scrollEl.style.setProperty('--calibration-header-height', `${Math.ceil(height)}px`);
      }
    };

    updateHeaderHeight();

    if (typeof ResizeObserver !== 'undefined') {
      this.headerResizeObserver = new ResizeObserver(() => updateHeaderHeight());
      this.headerResizeObserver.observe(headerEl);
    } else {
      const onResize = () => updateHeaderHeight();
      window.addEventListener('resize', onResize);
      this.destroy$.subscribe(() => window.removeEventListener('resize', onResize));
    }
  }
}
